<script setup lang="ts">
defineEmits(['showStoreStock'])

const { allocatedStore, qtyStoresWithStock, qtyStoresWithStockOrBackorder, storeStock, getItemStoreStock } = useStorelocator()
const { items } = useCart()

const allocatedStoreStock = computed<string | null>(() => {
  if (!allocatedStore.value) {
    return null
  }
  return storeStock.value?.stores?.find(item => item?.code === allocatedStore.value?.code)?.status || null
})

const containsBackorderableItem = computed(() => items.value.some(item => getItemStoreStock(String(item?.id))?.status === 'all-backorder'))
const containsPickupOnlyItem = computed(() => items.value.some(item => item?.product?.product?.isPickupOnly))
</script>

<template>
  <div class="cart-delivery-promises__wrapper">
    <div class="delivery-promise">
      <div class="delivery-promise__item delivery-promise__online">
        <div class="delivery-promise__icon">
          <NuxtIcon name="icon:truck" />
        </div>
        <div class="delivery-promise__content">
          <div class="delivery-promise__title">
            <span>{{ $t(storeStock?.status === 'in-stock' ? 'Quick delivery' : 'Delivery time applies') }}</span>
          </div>
          <div
            class="delivery-promise__availability"
          >
            <span
              class="delivery-promise__availability-bullet"
              :class="containsPickupOnlyItem ? 'backorder' : storeStock?.status"
            />
            <span v-if="containsPickupOnlyItem">{{ $t('Delivery not available') }}</span>
            <span v-else-if="storeStock?.status === 'all-backorder'">{{ $tc('all-backorder', storeStock?.deliveryTime || 0) }}</span>
            <span v-else>{{ $t(`cart-${storeStock?.status}`) }}</span>
          </div>
        </div>
      </div>
      <div class="delivery-promise__item delivery-promise__store">
        <div class="delivery-promise__icon">
          <NuxtIcon name="icon:stores" />
        </div>
        <div class="delivery-promise__content">
          <div class="delivery-promise__title">
            <span>{{ $t('Order and pickup') }}.</span>&nbsp;
            <button
              class="delivery-promise__select-store"
              @click="$emit('showStoreStock', storeStock)"
            >
              <span v-if="allocatedStore">{{ allocatedStore.name }}</span>
              <span v-else>{{ $t('Select your store') }}</span>
            </button>
          </div>
          <div
            v-if="allocatedStoreStock"
            class="delivery-promise__availability"
          >
            <span
              class="delivery-promise__availability-bullet"
              :class="allocatedStoreStock"
            />
            <span v-if="allocatedStoreStock === 'all-backorder'">{{ $tc('all-backorder', storeStock?.deliveryTime || 0) }}</span>
            <span v-else>{{ $t(`checkout-${allocatedStoreStock}`) }}</span>
          </div>
          <div
            v-else
            class="delivery-promise__availability"
          >
            <span
              class="delivery-promise__availability-bullet"
              :class="storeStock?.status"
            />
            <span v-if="storeStock?.status === 'all-backorder'">{{ $tc('all-backorder', storeStock?.deliveryTime || 0) }}</span>
            <span v-else-if="qtyStoresWithStock">{{ $tc('Stores with stock', qtyStoresWithStock) }}</span>
            <span v-else>{{ $t(`checkout-${storeStock?.status}`) }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!qtyStoresWithStockOrBackorder && !containsBackorderableItem"
      class="delivery-promise__disclaimer"
    >
      <NuxtIcon name="icon:warning" />
      <span class="delivery-promise__disclaimer-text">Er zijn geen winkels die je hele bestelling op voorraad hebben. Verwijder product(en) uit je winkelwagen om verder te gaan met bestellen. Plaats hier een aparte bestelling voor.</span>
    </div>
    <div
      v-else-if="qtyStoresWithStock && allocatedStoreStock?.includes('out-of-stock')"
      class="delivery-promise__disclaimer"
    >
      <NuxtIcon name="icon:warning" />
      <span
        v-if="allocatedStoreStock === 'all-out-of-stock'"
        class="delivery-promise__disclaimer-text"
      >
        Afhalen bij {{ allocatedStore.name }} is niet mogelijk omdat je bestelling hier niet op voorraad is. Selecteer een winkel met voorraad of kies voor bezorging om verder te gaan met bestellen.
      </span>
      <span v-else>
        Afhalen bij {{ allocatedStore.name }} is niet mogelijk omdat niet alle producten hier op voorraad zijn. Selecteer een winkel met voorraad, verwijder product(en) zonder voorraad uit de winkelwagen of kies voor bezorging om verder te gaan met bestellen.
      </span>
    </div>
  </div>
</template>

<style>
@import url("~layers/components/Cart/DeliveryPromise.css");
</style>
